import React, { useEffect, useState } from "react";
import { Mail, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { boardFetch } from "../api";
import { SectionHeader } from "../components";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const STATUS = {
  neu: "bg-champagne/15 text-champagne border-champagne/40",
  kontaktiert: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  erledigt: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};
const OPTIONS = ["neu", "kontaktiert", "erledigt"];

const Leads = () => {
  const [leads, setLeads] = useState(null);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("neu");

  useEffect(() => {
    boardFetch("/leads").then(setLeads).catch((e) => toast.error(e.message));
  }, []);

  const setStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await boardFetch(`/leads/${id}`, { method: "PUT", json: { status } });
    } catch (e) { toast.error(e.message); boardFetch("/leads").then(setLeads); }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      const res = await boardFetch("/leads/send-digest-now", { method: "POST" });
      if (res?.sent) toast.success(`Zusammenfassung an ${res.recipient} gesendet (${res.count})`);
      else toast.info(res?.reason || "Nichts gesendet");
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  if (!leads) return <div className="py-20 text-center text-white/40 animate-pulse">Leads werden geladen…</div>;

  const counts = { alle: leads.length, neu: 0, kontaktiert: 0, erledigt: 0 };
  leads.forEach((l) => { counts[l.status || "neu"] += 1; });
  const TABS = [["neu", "Neu"], ["kontaktiert", "Kontaktiert"], ["erledigt", "Erledigt"], ["alle", "Alle"]];
  const shown = filter === "alle" ? leads : leads.filter((l) => (l.status || "neu") === filter);

  return (
    <div data-testid="leads-page">
      <SectionHeader overline="Concierge" title="Leads" action={
        <div className="flex items-center gap-4">
          <span className="text-xs uppercase tracking-widest text-white/40">{leads.length} erfasst</span>
          <button onClick={sendDigest} disabled={sending} data-testid="leads-send-digest"
            className="inline-flex items-center gap-2 border border-champagne/40 px-4 py-2.5 text-xs uppercase tracking-widest text-champagne transition-colors hover:bg-champagne/10 disabled:opacity-40">
            <Send size={14} /> {sending ? "Sende…" : "Zusammenfassung testen"}
          </button>
        </div>
      } />

      <div className="mb-6 flex flex-wrap gap-2" data-testid="leads-filter">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} data-testid={`leads-filter-${key}`}
            className={`flex items-center gap-2 border px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
              filter === key ? "border-champagne bg-champagne/10 text-champagne" : "border-white/15 text-white/50 hover:text-white"}`}>
            {label}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${filter === key ? "bg-champagne/20 text-champagne" : "bg-white/10 text-white/50"}`}>{counts[key]}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="border border-white/10 bg-white/[0.02] py-16 text-center">
          <MessageSquare size={28} className="mx-auto text-white/20" />
          <p className="mt-4 text-sm text-white/45">
            {leads.length === 0 ? "Noch keine Leads über den KI-Concierge erfasst." : "Keine Leads in dieser Ansicht."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/10" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.03] text-left text-[11px] uppercase tracking-wider text-white/45">
                <th className="px-4 py-3 font-normal">Name</th>
                <th className="px-4 py-3 font-normal">E-Mail</th>
                <th className="px-4 py-3 font-normal">Anliegen</th>
                <th className="px-4 py-3 font-normal">Erfasst</th>
                <th className="px-4 py-3 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid="lead-row">
                  <td className="px-4 py-3 align-top text-white/85">{l.name || "—"}</td>
                  <td className="px-4 py-3 align-top">
                    {l.email ? (
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 text-champagne hover:underline">
                        <Mail size={13} /> {l.email}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 align-top text-white/60">{l.message || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3 align-top text-white/45">{fmt(l.created_at)}</td>
                  <td className="px-4 py-3 align-top">
                    <select value={l.status || "neu"} onChange={(e) => setStatus(l.id, e.target.value)}
                      data-testid="lead-status-select"
                      className={`cursor-pointer rounded-full border px-2.5 py-1 text-[11px] uppercase tracking-wider outline-none ${STATUS[l.status || "neu"]}`}>
                      {OPTIONS.map((o) => <option key={o} value={o} className="bg-[#141414] text-white">{o}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Leads;
